import { useCallback, useEffect, useRef, useState } from 'react'
import { checkWebsiteAvailability } from '../services/api'

const DEBOUNCE_MS = 600

/**
 * Whether the ad's website answers (WebsiteAvailability): checked while the address is typed,
 * and once more before a generation starts. `ensure()` resolves to 'reachable',
 * 'unreachable' or 'unknown' (the check itself failed: the user is not held back).
 */
export function useWebsiteAvailability(apiBase, url, enabled) {
  const [state, setState] = useState({ url: '', status: 'idle' })
  const results = useRef(new Map())
  const pending = useRef(null)
  const address = (url || '').trim()

  const check = useCallback((value) => {
    const known = results.current.get(value)
    if (known) {
      setState({ url: value, status: known })
      return Promise.resolve(known)
    }
    pending.current?.abort()
    const controller = new AbortController()
    pending.current = controller
    setState({ url: value, status: 'checking' })
    return checkWebsiteAvailability(apiBase, value, controller.signal)
      .then((data) => {
        const status = data?.status === 'reachable' ? 'reachable' : 'unreachable'
        results.current.set(value, status)
        if (pending.current === controller) setState({ url: value, status })
        return status
      })
      .catch(() => {
        if (controller.signal.aborted) return 'unknown'
        if (pending.current === controller) setState({ url: value, status: 'idle' })
        return 'unknown'
      })
  }, [apiBase])

  useEffect(() => {
    if (!enabled || !address) {
      pending.current?.abort()
      setState({ url: '', status: 'idle' })
      return undefined
    }
    const timer = window.setTimeout(() => { check(address) }, DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [address, enabled, check])

  const ensure = useCallback(() => (
    enabled && address ? check(address) : Promise.resolve('unknown')
  ), [address, enabled, check])

  return { status: state.url === address ? state.status : 'idle', ensure }
}
